/**
 * WorkspaceSettingsTemplate — layout shell for workspace settings.
 * Two-column: profile card + plan usage | ListPanel. Pure layout, no hooks.
 */

import React from "react";
import { UsersRound, Loader2, CalendarDays } from "lucide-react";
import { Sidebar } from "../../organisms/Sidebar";
import { ListPanel } from "../../organisms/ListPanel";
import { Card } from "../../atoms/Card";
import { Avatar } from "../../atoms/Avatar";
import { Typography } from "../../atoms/Typography";
import { Divider } from "../../atoms/Divider";
import { Tag } from "../../atoms/Tag";
import type { WorkspaceSettingsTemplateProps } from "./WorkspaceSettingsTemplate.types";

export const WorkspaceSettingsTemplate: React.FC<WorkspaceSettingsTemplateProps> = ({
  isOpen,
  onClose,
  title = "Workspace",
  loading = false,
  showHeaderIcon = true,
  showCloseButton = true,
  profileCardProps,
  listPanelProps,
}) => {
  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      width="xl"
      ariaLabel={title}
      headerTitle={title}
      headerIcon={showHeaderIcon ? <UsersRound /> : undefined}
      showHeaderIcon={showHeaderIcon}
      showCloseButton={showCloseButton}
    >
      {loading ? (
        <div className="flex items-center justify-center flex-1 min-h-0">
          <Loader2 className="w-8 h-8 animate-spin text-purple" />
        </div>
      ) : (
        <div className="flex flex-1 min-h-0 overflow-hidden flex-col lg:flex-row">
          <aside className="w-full lg:w-80 flex-shrink-0 flex flex-col gap-6 py-6 px-4 bg-white border-b border-gray-200 lg:border-b-0 lg:border-r">
            <Card variant="outlined" padding="md" rounded="xl">
              <section className="flex flex-col items-center text-center" aria-label="Profile">
                <Avatar initials={profileCardProps.initials} variant="gradient" size="2xl" className="mt-0" />
                <Typography variant="h4" weight="semibold" color="primary" className="mt-4">
                  {profileCardProps.name}
                </Typography>
                <Tag intent="success" variant="subtle" className="mt-2 capitalize">
                  {profileCardProps.status}
                </Tag>
                <Typography variant="body-sm" weight="semibold" color="secondary" className="mt-3">
                  {profileCardProps.memberCount} {profileCardProps.memberCount === 1 ? "member" : "members"}
                </Typography>
              </section>
            </Card>

            <Divider className="my-2" />

            <section className="w-full space-y-3" aria-label="Plan usage">
              <div className="flex items-center justify-between gap-2 w-full">
                <Typography variant="body-sm" weight="semibold" color="primary" className="capitalize">
                  {profileCardProps.planName}
                </Typography>
                <Tag
                  intent={profileCardProps.planExpired ? "error" : "success"}
                  variant="subtle"
                  className="capitalize"
                >
                  {profileCardProps.planExpired ? "Expired" : "Active"}
                </Tag>
              </div>
              <div className="flex items-center gap-2">
                <CalendarDays className="w-3 text-dark-invirtus/70" aria-hidden />
                <Typography variant="body-xs" color="secondary" className="block">
                  {profileCardProps.planExpiresAt}
                </Typography>
              </div>
              <div className="space-y-3">
                <div className="flex flex-row items-center justify-between gap-1 w-full">
                  <Typography variant="body-sm" weight="medium" color="primary">
                    CV Scoring
                  </Typography>
                  <Typography variant="body-lg" weight="bold" color="primary" className="text-purple">
                    {profileCardProps.cvLimit === -1
                      ? `${profileCardProps.cvUsed} / ∞`
                      : `${profileCardProps.cvUsed} / ${profileCardProps.cvLimit}`}
                  </Typography>
                </div>
                <Divider />
                <div className="flex flex-row items-center justify-between gap-1 w-full">
                  <Typography variant="body-sm" weight="medium" color="primary">
                    AI Interviews
                  </Typography>
                  <Typography variant="body-lg" weight="bold" color="primary" className="text-purple">
                  {profileCardProps.interviewLimit === -1
                      ? `${profileCardProps.interviewUsed} / ∞`
                      : `${profileCardProps.interviewUsed} / ${profileCardProps.interviewLimit}`}
                  </Typography>
                </div>
              </div>
            </section>
          </aside>
          <main className="flex-1 min-w-0 flex flex-col overflow-hidden min-h-0 bg-background-gray">
            <ListPanel {...listPanelProps} />
          </main>
        </div>
      )}
    </Sidebar>
  );
};
